import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ArrowLeft,
  CreditCard,
  Download,
  FileText,
  Loader2,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { TABLE_CELL_TYPE, TABLE_HEADER_TYPE, TYPE, UI_MUTED, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { useClinicUsersListForClinics } from '@/hooks/useClinicUsers';
import { useSubClinics } from '@/hooks/useMultiClinic';
import { useCashierReport, useCashierReportExport } from '@/hooks/useCashierReport';
import {
  cashierPractitionerOptions,
  clinicIdsForCashierStaff,
} from '@/lib/reporting/cashierPractitioners';
import { formatMoneyCents, downloadBlob } from '@/lib/reporting/format';
import { formatPeriodLabel, resolveReportPeriod } from '@/lib/reporting/periods';
import { formatZonedDate, formatZonedDateTime } from '@/lib/reporting/timezone';
import {
  countCashierFilters,
  formatCashierBucketLabel,
} from '@/lib/reporting/cashierEngine';
import {
  cashierReportToCsv,
  cashierReportToExcelXml,
  cashierReportToPdf,
  type CashierExportLabels,
} from '@/lib/reporting/exportCashier';
import {
  EMPTY_CASHIER_FILTERS,
  type CashierFilterState,
  type CashierOutstandingInvoice,
  type CashierPaymentRow,
  type CashierPeriodPreset,
  type CashierReportResult,
} from '@/lib/reporting/cashierTypes';
import {
  isKnownPaymentMethod,
  PAYMENT_METHOD_LABEL_KEYS,
  type PaymentChannel,
} from '@/lib/reporting/paymentMethods';
import { MultiSelectFilter } from './MultiSelectFilter';
import { PeriodFilter } from './PeriodFilter';
import { cn } from '@/utils/cn';

const PERIODS: CashierPeriodPreset[] = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_year',
  'last_year',
  'custom',
];

function methodLabel(key: string, t: TFunction): string {
  if (isKnownPaymentMethod(key)) return t(PAYMENT_METHOD_LABEL_KEYS[key]);
  const cleaned = key.replace(/_/g, ' ').trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : t('reports.cashier.methods.other');
}

function exportLabels(result: CashierReportResult, t: TFunction): CashierExportLabels {
  const methodLabels: Record<string, string> = {};
  for (const method of result.methods) {
    methodLabels[method.method_key] = methodLabel(method.method_key, t);
  }
  for (const [key, labelKey] of Object.entries(PAYMENT_METHOD_LABEL_KEYS)) {
    methodLabels[key] = t(labelKey);
    methodLabels[labelKey] = t(labelKey);
  }
  return {
    title: t('reports.types.cashier.label'),
    subtitle: t('reports.types.cashier.description'),
    period: t('reports.export.period'),
    generated: t('reports.export.generated'),
    clinic: t('reports.export.clinic'),
    brand: 'Clinio',
    keyFigures: t('reports.export.key_figures'),
    totalReceived: t('reports.cashier.total_received'),
    refunds: t('reports.cashier.refunds'),
    netReceived: t('reports.cashier.net_received'),
    outstanding: t('reports.cashier.outstanding'),
    outstandingHint: t('reports.cashier.outstanding_hint'),
    outstandingInvoices: t('reports.cashier.outstanding_invoices'),
    outstandingEmpty: t('reports.cashier.outstanding_empty'),
    outstandingTotal: t('reports.cashier.outstanding_total'),
    issued: t('reports.cashier.issued'),
    due: t('reports.cashier.due'),
    invoiceTotal: t('reports.cashier.invoice_total'),
    invoicePaid: t('reports.cashier.invoice_paid'),
    invoiceStatusLabels: {
      issued: t('invoices.status_issued'),
      partial: t('invoices.status_partial'),
      overdue: t('invoices.status_overdue'),
    },
    transactions: t('reports.cashier.transactions'),
    paymentMethods: t('reports.cashier.payment_methods'),
    method: t('reports.cashier.method'),
    amount: t('reports.cashier.amount'),
    count: t('reports.cashier.count'),
    total: t('reports.total'),
    dailySummary: t('reports.cashier.period_summary'),
    date: t('reports.export.date'),
    received: t('reports.cashier.received'),
    net: t('reports.cashier.net'),
    refundSection: t('reports.cashier.refunds_section'),
    refundCount: t('reports.cashier.refund_count'),
    cashReceived: t('reports.cashier.cash_received'),
    cashTransactions: t('reports.cashier.cash_transactions'),
    clinioPay: t('reports.cashier.clinio_pay'),
    feesUnavailable: t('reports.cashier.fees_unavailable'),
    payoutUnavailable: t('reports.cashier.payout_unavailable'),
    transactionDetail: t('reports.cashier.transaction_detail'),
    dateTime: t('reports.cashier.datetime'),
    patient: t('reports.export.patient'),
    invoice: t('reports.export.invoice'),
    channel: t('reports.cashier.channel'),
    reference: t('reports.cashier.reference'),
    status: t('reports.cashier.status'),
    practitioner: t('reports.columns.practitioner'),
    clinicColumn: t('reports.export.clinic'),
    channelClinioPay: t('reports.cashier.channel_clinio_pay'),
    channelOther: t('reports.cashier.channel_other'),
    statusReceived: t('reports.cashier.status_received'),
    statusRefunded: t('reports.cashier.status_refunded'),
    truncated: t('reports.cashier.truncated'),
    footerReport: t('reports.types.cashier.label'),
    page: t('reports.export.page'),
    pageOf: t('reports.export.page_of'),
    methodLabels,
  };
}

function SummaryCard({
  label,
  value,
  hint,
  onClick,
}: {
  label: string;
  value: string;
  hint?: string;
  onClick?: () => void;
}) {
  const className =
    'rounded-card bg-ds-card p-s4 shadow-card';
  const body = (
    <>
      <p className={UI_SECONDARY}>{label}</p>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-ds-text">{value}</p>
      {hint ? <p className={`mt-1 ${UI_SECONDARY}`}>{hint}</p> : null}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${className} w-full text-left hover:border-ds-teal-line`}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}

export function CashierReportPage() {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const [filters, setFilters] = useState<CashierFilterState>(EMPTY_CASHIER_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [includePdfDetails, setIncludePdfDetails] = useState(false);

  const locale = i18n.language === 'da' ? 'da-DK' : 'en-GB';
  const staffClinicIds = useMemo(
    () => clinicIdsForCashierStaff(activeClinicId, filters.locationIds),
    [activeClinicId, filters.locationIds],
  );
  const membersQ = useClinicUsersListForClinics(staffClinicIds, { includeInactive: false });
  const locationsQ = useSubClinics(activeClinicId);
  const reportQ = useCashierReport(activeClinicId, filters, Boolean(activeClinicId));
  const exporter = useCashierReportExport(activeClinicId, filters);

  const practitioners = useMemo(
    () => cashierPractitionerOptions(membersQ.data ?? []),
    [membersQ.data],
  );

  const locationOptions = useMemo(() => {
    const subs = locationsQ.data ?? [];
    if (subs.length === 0) return [];
    return [
      ...(activeClinicId ? [{ id: activeClinicId, label: t('reports.filters.this_clinic') }] : []),
      ...subs.map((clinic) => ({ id: clinic.id, label: clinic.name })),
    ];
  }, [activeClinicId, locationsQ.data, t]);

  const period = useMemo(
    () => resolveReportPeriod(filters.period, filters.customFrom, filters.customTo),
    [filters.period, filters.customFrom, filters.customTo],
  );

  const result = reportQ.data ?? null;
  const currency = result?.currency ?? 'DKK';
  const money = (cents: number) => formatMoneyCents(cents, currency, locale);
  const activeFilterCount = countCashierFilters(filters);

  const methodOptions = useMemo(() => {
    const keys = new Set<string>(result?.availableMethods ?? result?.methods.map((m) => m.method_key) ?? []);
    return [...keys].map((id) => ({ id, label: methodLabel(id, t) }));
  }, [result, t]);

  const applySearch = () => {
    setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
  };

  const outstandingColumns: TableColumn<CashierOutstandingInvoice>[] = [
    {
      id: 'invoice',
      header: t('reports.export.invoice'),
      cell: (invoice) => (
        <Link
          to={`/invoices/${invoice.id}`}
          tabIndex={-1}
          className={cn(TYPE.body, 'block font-medium text-ds-text')}
        >
          {invoice.invoice_number ?? t('reports.export.invoice')}
        </Link>
      ),
    },
    {
      id: 'patient',
      header: t('reports.export.patient'),
      cell: (invoice) => invoice.patient_name ?? '—',
    },
    {
      id: 'issued',
      header: t('reports.cashier.issued'),
      cell: (invoice) =>
        result ? formatZonedDate(invoice.issued_at, locale, result.timezone) : '—',
    },
    {
      id: 'due',
      header: t('reports.cashier.due'),
      cell: (invoice) =>
        result ? formatZonedDate(invoice.due_at, locale, result.timezone) : '—',
    },
    {
      id: 'total',
      header: t('reports.cashier.invoice_total'),
      numeric: true,
      cell: (invoice) => money(invoice.total_cents),
    },
    {
      id: 'paid',
      header: t('reports.cashier.invoice_paid'),
      numeric: true,
      cell: (invoice) => money(invoice.paid_cents),
    },
    {
      id: 'outstanding',
      header: t('reports.cashier.outstanding'),
      numeric: true,
      cell: (invoice) => (
        <span className="font-semibold">{money(invoice.outstanding_cents)}</span>
      ),
    },
    {
      id: 'status',
      header: t('reports.cashier.status'),
      cell: (invoice) => t(`invoices.status_${invoice.status}`, invoice.status),
    },
  ];

  const transactionColumns: TableColumn<CashierPaymentRow>[] = [
    {
      id: 'datetime',
      header: t('reports.cashier.datetime'),
      cell: (txn) =>
        result ? formatZonedDateTime(txn.payment_date, locale, result.timezone) : '—',
    },
    {
      id: 'patient',
      header: t('reports.export.patient'),
      cell: (txn) => txn.patient_name ?? '—',
    },
    {
      id: 'invoice',
      header: t('reports.export.invoice'),
      cell: (txn) =>
        txn.invoice_id ? (
          <Link
            to={`/invoices/${txn.invoice_id}`}
            tabIndex={-1}
            className={cn(TYPE.body, 'block font-medium text-ds-text')}
          >
            {txn.invoice_number ?? t('reports.export.invoice')}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      id: 'method',
      header: t('reports.cashier.method'),
      cell: (txn) => methodLabel(txn.payment_method_key, t),
    },
    {
      id: 'channel',
      header: t('reports.cashier.channel'),
      cell: (txn) =>
        txn.channel === 'clinio_pay'
          ? t('reports.cashier.channel_clinio_pay')
          : t('reports.cashier.channel_other'),
    },
    {
      id: 'amount',
      header: t('reports.cashier.amount'),
      numeric: true,
      cell: (txn) => (
        <span className={cn(txn.kind === 'refund' && 'text-ds-red-ink')}>
          {money(txn.kind === 'refund' ? -Math.abs(txn.amount_cents) : txn.amount_cents)}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('reports.cashier.status'),
      cell: (txn) =>
        txn.status === 'refunded'
          ? t('reports.cashier.status_refunded')
          : t('reports.cashier.status_received'),
    },
  ];

  const exportFile = async (kind: 'csv' | 'xls' | 'pdf') => {
    const data = await exporter.fetch();
    const labels = exportLabels(data, t);
    const stamp = new Date().toISOString().slice(0, 10);
    if (kind === 'csv') {
      downloadBlob(
        new Blob([cashierReportToCsv(data, labels, locale)], { type: 'text/csv;charset=utf-8' }),
        `cashier-reconciliation-${stamp}.csv`,
      );
      return;
    }
    if (kind === 'xls') {
      downloadBlob(
        new Blob([cashierReportToExcelXml(data, labels, locale)], { type: 'application/vnd.ms-excel' }),
        `cashier-reconciliation-${stamp}.xls`,
      );
      return;
    }
    downloadBlob(
      new Blob(
        [
          new Uint8Array(
            cashierReportToPdf(data, labels, {
              includeDetails: includePdfDetails || data.transactions.length <= 80,
              locale,
            }),
          ),
        ],
        { type: 'application/pdf' },
      ),
      `cashier-reconciliation-${stamp}.pdf`,
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/economy/reports"
            className={`inline-flex items-center ${UI_MUTED} hover:text-ds-text`}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('reports.back')}
          </Link>
          <h2 className={`mt-2 ${UI_SECTION}`}>{t('reports.types.cashier.label')}</h2>
          <p className={`mt-1 ${UI_MUTED}`}>{t('reports.types.cashier.description')}</p>
          <p className={`mt-2 ${TYPE.label} text-ds-teal`}>
            {formatPeriodLabel(period, locale)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => void exportFile('csv')}
            disabled={!result || reportQ.isFetching}
          >
            <Download className="mr-1 h-4 w-4" />
            {t('reports.export.csv')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void exportFile('xls')}
            disabled={!result || reportQ.isFetching}
          >
            <Download className="mr-1 h-4 w-4" />
            {t('reports.export.excel')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void exportFile('pdf')}
            disabled={!result || reportQ.isFetching}
          >
            <Download className="mr-1 h-4 w-4" />
            {t('reports.export.pdf')}
          </Button>
        </div>
      </div>

      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
        <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
          <Input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applySearch();
            }}
            onBlur={applySearch}
            placeholder={t('reports.cashier.search_placeholder')}
            aria-label={t('reports.cashier.search_placeholder')}
          />
        </div>
        <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
          <PeriodFilter
            periods={PERIODS}
            value={filters.period}
            customFrom={filters.customFrom}
            customTo={filters.customTo}
            onChange={(periodId) => setFilters((prev) => ({ ...prev, period: periodId }))}
            onCustomChange={({ customFrom, customTo }) =>
              setFilters((prev) => ({ ...prev, customFrom, customTo }))
            }
            labelFor={(periodId) => t(`reports.periods.${periodId}`)}
            locale={locale}
            ariaLabel={t('reports.cashier.date_range')}
            fromLabel={t('reports.cashier.from')}
            toLabel={t('reports.cashier.to')}
          />
          <MultiSelectFilter
            label={t('reports.filters.payment_method')}
            allLabel={t('reports.cashier.all_methods')}
            options={methodOptions}
            selectedIds={filters.paymentMethods}
            onChange={(ids) => setFilters((prev) => ({ ...prev, paymentMethods: ids }))}
            emptyLabel={t('reports.cashier.no_methods')}
          />
          <MultiSelectFilter
            label={t('reports.cashier.channel')}
            allLabel={t('reports.cashier.all_channels')}
            options={[
              { id: 'clinio_pay', label: t('reports.cashier.channel_clinio_pay') },
              { id: 'other', label: t('reports.cashier.channel_other') },
            ]}
            selectedIds={filters.channels}
            onChange={(ids) =>
              setFilters((prev) => ({ ...prev, channels: ids as PaymentChannel[] }))
            }
          />
          <MultiSelectFilter
            label={t('reports.filters.practitioner')}
            allLabel={t('reports.filters.all_practitioners')}
            options={practitioners}
            selectedIds={filters.practitionerIds}
            onChange={(ids) => setFilters((prev) => ({ ...prev, practitionerIds: ids }))}
            emptyLabel={t('reports.filters.no_practitioners')}
          />
          {locationOptions.length > 0 ? (
            <MultiSelectFilter
              label={t('reports.filters.location')}
              allLabel={t('reports.filters.all_locations')}
              options={locationOptions}
              selectedIds={filters.locationIds}
              onChange={(ids) => setFilters((prev) => ({ ...prev, locationIds: ids }))}
            />
          ) : null}
          <MultiSelectFilter
            label={t('reports.cashier.status')}
            allLabel={t('reports.cashier.all_statuses')}
            options={[
              { id: 'received', label: t('reports.cashier.status_received') },
              { id: 'refunded', label: t('reports.cashier.status_refunded') },
            ]}
            selectedIds={filters.statuses}
            onChange={(ids) =>
              setFilters((prev) => ({
                ...prev,
                statuses: ids as CashierFilterState['statuses'],
              }))
            }
          />
          <label className={`flex items-center gap-s2 ${UI_MUTED}`}>
            <input
              type="checkbox"
              checked={includePdfDetails}
              onChange={(event) => setIncludePdfDetails(event.target.checked)}
              className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
            />
            {t('reports.export.include_details')}
          </label>
          {activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={() => {
                setFilters(EMPTY_CASHIER_FILTERS);
                setSearchInput('');
              }}
              className={`${UI_MUTED} hover:text-ds-text`}
            >
              {t('reports.clear_all')}
            </button>
          ) : null}
        </div>
      </div>

      {reportQ.error && <ErrorAlert message={t('reports.load_error')} />}

      {reportQ.isFetching && !result && (
        <div className="flex items-center justify-center py-12 text-ds-muted">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {result && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SummaryCard
              label={t('reports.cashier.total_received')}
              value={money(result.summary.total_received_cents)}
            />
            <SummaryCard
              label={t('reports.cashier.refunds')}
              value={money(result.summary.refunds_cents)}
            />
            <SummaryCard
              label={t('reports.cashier.net_received')}
              value={money(result.summary.net_received_cents)}
            />
            <SummaryCard
              label={t('reports.cashier.outstanding')}
              value={money(result.summary.outstanding_cents)}
              hint={t('reports.cashier.outstanding_hint')}
              onClick={() =>
                document.getElementById('cashier-outstanding-invoices')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
              }
            />
            <SummaryCard
              label={t('reports.cashier.transactions')}
              value={String(result.summary.transaction_count)}
              hint={
                result.summary.refund_count > 0
                  ? t('reports.cashier.refund_count_hint', { count: result.summary.refund_count })
                  : undefined
              }
            />
          </div>

          <section className="rounded-card bg-ds-card p-s4 shadow-card">
            <h3 className={UI_SECTION}>
              {t('reports.cashier.payment_methods')}
            </h3>
            {result.methods.length === 0 ? (
              <p className={`mt-3 ${UI_MUTED}`}>{t('reports.cashier.empty')}</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className={`min-w-full ${TYPE.body}`}>
                  <thead>
                    <tr>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-left')}>
                        {t('reports.cashier.method')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.received')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.refunds')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.net')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.transactions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.methods.map((method) => (
                      <tr key={method.method_key}>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 font-medium')}>
                          {methodLabel(method.method_key, t)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {money(method.received_cents)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {money(method.refund_cents)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {money(method.net_cents)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {method.transaction_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-ds-teal-tint font-semibold">
                      <td className="px-s3 py-s3">{t('reports.total')}</td>
                      <td className="px-s3 py-s3 text-right tabular-nums">
                        {money(result.summary.total_received_cents)}
                      </td>
                      <td className="px-s3 py-s3 text-right tabular-nums">{money(result.summary.refunds_cents)}</td>
                      <td className="px-s3 py-s3 text-right tabular-nums">
                        {money(result.summary.net_received_cents)}
                      </td>
                      <td className="px-s3 py-s3 text-right tabular-nums">{result.summary.transaction_count}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>

          <section id="cashier-outstanding-invoices">
            <h3 className={cn(UI_SECTION, 'mb-s3')}>{t('reports.cashier.outstanding_invoices')}</h3>
            {result.outstandingInvoices.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<FileText className="h-7 w-7" />}
                  title={t('reports.cashier.outstanding_empty')}
                />
              </Card>
            ) : (
              <Card className="p-0 nav:p-0">
                <ResponsiveTable
                  columns={outstandingColumns}
                  rows={result.outstandingInvoices}
                  getRowKey={(invoice) => invoice.id}
                  getRowHref={(invoice) => `/invoices/${invoice.id}`}
                  getRowLabel={(invoice) =>
                    invoice.invoice_number ?? invoice.patient_name ?? t('reports.export.invoice')
                  }
                  empty={null}
                />
                <div className="flex items-center justify-between border-t border-ds-line px-s3 py-s3">
                  <span className={cn(TYPE.body, 'font-medium text-ds-text')}>
                    {t('reports.cashier.outstanding_total')}
                  </span>
                  <span className="tabular-nums font-semibold text-ds-text">
                    {money(result.summary.outstanding_cents)}
                  </span>
                </div>
              </Card>
            )}
          </section>

          {result.refunds.length > 0 && (
            <section className="rounded-card bg-ds-card p-s4 shadow-card">
              <h3 className={UI_SECTION}>
                {t('reports.cashier.refunds_section')}
              </h3>
              <ul className={`mt-4 divide-y divide-ds-line ${TYPE.body}`}>
                {result.refunds.map((method) => (
                  <li key={method.method_key} className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium">{methodLabel(method.method_key, t)}</p>
                      <p className={UI_MUTED}>
                        {t('reports.cashier.refund_count', { count: method.refund_count })}
                      </p>
                    </div>
                    <p className="tabular-nums font-semibold">{money(method.refund_cents)}</p>
                  </li>
                ))}
                <li className="flex items-center justify-between py-3 font-semibold">
                  <span>{t('reports.cashier.total_refunds')}</span>
                  <span className="tabular-nums">{money(result.summary.refunds_cents)}</span>
                </li>
              </ul>
            </section>
          )}

          {result.periodRows.length > 0 && (
            <section className="overflow-hidden rounded-card bg-ds-card shadow-card">
              <div className="px-5 py-4">
                <h3 className={UI_SECTION}>
                  {t('reports.cashier.period_summary')}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className={`min-w-full ${TYPE.body}`}>
                  <thead>
                    <tr>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-left')}>
                        {t('reports.export.date')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.received')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.refunds')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.net')}
                      </th>
                      <th className={cn(TABLE_HEADER_TYPE, 'border-b border-ds-line px-s3 pb-s2 text-right')}>
                        {t('reports.cashier.transactions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.periodRows.map((row) => (
                      <tr key={row.bucket}>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3')}>
                          {formatCashierBucketLabel(row.bucket, result.grain, locale)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {money(row.received_cents)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {money(row.refund_cents)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {money(row.net_cents)}
                        </td>
                        <td className={cn(TABLE_CELL_TYPE, 'border-b border-ds-line px-s3 py-s3 text-right tabular-nums')}>
                          {row.transaction_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-ds-teal-tint font-semibold">
                      <td className="px-s3 py-s3">{t('reports.total')}</td>
                      <td className="px-s3 py-s3 text-right tabular-nums">
                        {money(result.summary.total_received_cents)}
                      </td>
                      <td className="px-s3 py-s3 text-right tabular-nums">{money(result.summary.refunds_cents)}</td>
                      <td className="px-s3 py-s3 text-right tabular-nums">
                        {money(result.summary.net_received_cents)}
                      </td>
                      <td className="px-s3 py-s3 text-right tabular-nums">{result.summary.transaction_count}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}

          <section>
            <h3 className={cn(UI_SECTION, 'mb-s3')}>{t('reports.cashier.transaction_detail')}</h3>
            {result.transactionsTruncated ? (
              <p className={cn(TYPE.body, 'mb-s3 text-ds-amber-ink')}>{t('reports.cashier.truncated')}</p>
            ) : null}
            {result.transactions.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<CreditCard className="h-7 w-7" />}
                  title={t('reports.cashier.empty')}
                />
              </Card>
            ) : (
              <Card className="p-0 nav:p-0">
                <ResponsiveTable
                  columns={transactionColumns}
                  rows={result.transactions}
                  getRowKey={(txn) => txn.id}
                  getRowHref={(txn) => (txn.invoice_id ? `/invoices/${txn.invoice_id}` : undefined)}
                  getRowLabel={(txn) =>
                    txn.invoice_number ?? txn.patient_name ?? t('reports.cashier.transaction_detail')
                  }
                  empty={null}
                />
              </Card>
            )}
          </section>
        </>
      )}
    </div>
  );
}
